import { useLocation, useSearchParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { useWorkspaceContextStore } from "@presentation/stores/WorkspaceContextStore";
import { useWorkspaceFilterStore, type WorkspaceFilterScope, type WorkspaceFilterSelection } from "@presentation/stores/WorkspaceFilterStore";
import { usePermissionStore } from "@presentation/stores/PermissionStore";

export const allowedWorkspaceScopes = (pathname: string, isAdmin: boolean): WorkspaceFilterScope[] => {
  if (pathname === "/about") return ["deployment"];
  if (pathname === "/teams") return ["team"];
  if (pathname === "/job-setup") return ["project"];
  if (pathname === "/settings") return isAdmin ? ["deployment", "team", "project"] : ["team", "project"];
  return ["team", "project"];
};

export const useWorkspaceFilters = () => {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const { pageScope, lastTeamId, lastProjectSelection } = useWorkspaceFilterStore(
    useShallow((state) => ({
      pageScope: state.pageScopes[pathname],
      lastTeamId: state.lastTeamId,
      lastProjectSelection: state.lastProjectSelection,
    })),
  );
  const workspace = useWorkspaceContextStore(
    useShallow((state) => ({
      teams: state.teams,
      projects: state.projects,
      activeTeamId: state.activeTeamId,
      activeProjectId: state.activeProjectId,
      status: state.status,
    })),
  );
  const isAdmin = usePermissionStore((state) => state.isAdmin);
  const allowedScopes = allowedWorkspaceScopes(pathname, isAdmin);
  const requiresSelection = pathname === "/settings" || pathname === "/job-setup";
  const hasExplicitFilters = ["scope", "teamId", "projectId"].some((key) => params.has(key));
  const requestedScope = params.get("scope");

  let defaultScope: WorkspaceFilterScope = "team";
  if (requiresSelection || pathname === "/jobs") {
    if (workspace.activeProjectId) defaultScope = "project";
    else if (isAdmin && pathname === "/settings" && !workspace.activeTeamId) defaultScope = "deployment";
  }

  let scope: WorkspaceFilterScope = pageScope ?? defaultScope;
  if (!allowedScopes.includes(scope)) scope = allowedScopes[0];

  let teamId = scope === "project" ? (lastProjectSelection?.teamId ?? workspace.activeTeamId) : (lastTeamId ?? (requiresSelection ? workspace.activeTeamId : ""));
  let projectId = scope === "project" ? (lastProjectSelection?.projectId ?? workspace.activeProjectId) : "";

  if (!hasExplicitFilters && workspace.status === "loaded") {
    const hasTeam = workspace.teams.some((team) => team.id === teamId) || workspace.projects.some((project) => project.teamId === teamId);
    if (teamId && !hasTeam) teamId = workspace.activeTeamId;
    if (projectId && workspace.projects.every((project) => project.id !== projectId)) projectId = workspace.activeProjectId;
  }

  if (hasExplicitFilters) {
    if (requestedScope && ["deployment", "team", "project"].includes(requestedScope)) scope = requestedScope as WorkspaceFilterScope;
    else scope = params.get("projectId") ? "project" : "team";
    teamId = params.get("teamId") ?? "";
    projectId = params.get("projectId") ?? "";
  }

  if (!allowedScopes.includes(scope)) scope = allowedScopes[0];
  if (scope !== "project") projectId = "";
  if (scope === "deployment") teamId = "";
  if (scope === "project" && requiresSelection && !projectId) {
    projectId =
      workspace.projects.find((project) => project.id === workspace.activeProjectId && (!teamId || project.teamId === teamId))?.id ??
      workspace.projects.find((project) => !teamId || project.teamId === teamId)?.id ??
      "";
  }
  if (scope === "project" && projectId) teamId = workspace.projects.find((project) => project.id === projectId)?.teamId ?? teamId;
  if (scope === "team" && requiresSelection && !teamId) teamId = workspace.activeTeamId || workspace.teams[0]?.id || "";

  const selection: WorkspaceFilterSelection = { scope, teamId, projectId };
  return { ...selection, allowedScopes, requiresSelection, hasExplicitFilters, lastTeamId, lastProjectSelection, workspace };
};
